import React from 'react';
import './PrivacyPolicy.css';

export function PrivacyPolicyContent() {
    return (
        <>
            <p className="privacy-effective-date">Last updated: May 26, 2026</p>

            <section>
                <h2>Overview</h2>
                <p>
                    This Privacy Policy explains how Chloromance handles information when you use the Chloromance mobile
                    application or the Chloromance experience on this website. Chloromance is designed for plant care tracking,
                    including plant profiles, watering status, WiltPower, plant locations, weather-aware watering estimates,
                    optional reminders, and account-based plant backup.
                </p>
                <p>
                    Chloromance is developed by Prapti Setty. For privacy questions or requests, email{' '}
                    <a href="mailto:praptisetty11@gmail.com">praptisetty11@gmail.com</a>.
                </p>
            </section>

            <section>
                <h2>Information We Collect</h2>
                <p>Chloromance may process the following information:</p>
                <ul>
                    <li>
                        <strong>Account information:</strong> your email address and authentication user ID when you create or
                        sign in to a Chloromance account.
                    </li>
                    <li>
                        <strong>Plant care information:</strong> plant names, plant types, plant locations, watering frequency,
                        watering timestamps, notes, and hydration status.
                    </li>
                    <li>
                        <strong>Location information:</strong> latitude and longitude only if you choose to use your current
                        location for weather-based hydration estimates.
                    </li>
                    <li>
                        <strong>Device permissions:</strong> notification permission status when you choose to enable reminders.
                    </li>
                    <li>
                        <strong>App preferences:</strong> theme, reminder preference, selected weather location, and similar
                        settings.
                    </li>
                    <li>
                        <strong>Authentication session data:</strong> information needed to keep you signed in on your device
                        or browser.
                    </li>
                    <li>
                        <strong>Payment Information:</strong> If you purchase a premium subscription or feature, we may process 
                        transaction metadata (such as subscription status or purchase history). Please note that we do not collect, 
                        store, or handle raw payment card numbers or billing details directly.
                    </li>
                </ul>
            </section>

            <section>
                <h2>How We Use Information</h2>
                <p>We use information to:</p>
                <ul>
                    <li>Show your plant list and hydration dashboard.</li>
                    <li>Calculate WiltPower, thirsty/critical states, and watering schedules.</li>
                    <li>Back up and restore your latest plant records for your signed-in account.</li>
                    <li>Fetch local weather data when you request weather-aware estimates.</li>
                    <li>Send optional local notifications for thirsty or critical plants.</li>
                    <li>Remember app preferences such as theme and saved location.</li>
                    <li>Provide, manage, and verify your access to premium or paid subscription features.</li>
                </ul>
            </section>

            <section>
                <h2>Where Information Is Stored</h2>
                <p>
                    Chloromance stores account information and the latest plant care records in Supabase so your signed-in
                    account can restore plant names, plant details, and most recent watering data if local app storage is cleared
                    or lost. Chloromance also keeps a local copy on your device or browser using local storage so the app can
                    load quickly and continue to show your recent data. Clearing browser data, deleting the app, or resetting
                    device storage may remove the local copy, but signed-in accounts can retrieve cloud-backed plant records.
                </p>
                <p>
                    App preferences such as theme, reminder preference, weather coordinates, notification state, and
                    authentication session data may be stored locally on your device or browser.
                </p>
            </section>

            <section>
                <h2>Payment Processing</h2>
                <p>
                    We may offer paid features or subscriptions within Chloromance. When you make a purchase, we utilize 
                    third-party payment processors (such as Stripe, the Apple App Store, or Google Play Billing) for payment 
                    handling. Their use of your personal information is governed by their respective privacy policies. 
                    These third-party platforms conform to the standards set by PCI-DSS as managed by the PCI Security Standards 
                    Council, which is a joint effort of brands like Visa, Mastercard, American Express, and Discover to ensure 
                    the secure handling of financial data.
                </p>
            </section>

            <section>
                <h2>Security</h2>
                <p>
                    Chloromance relies on Supabase authentication and database security controls to protect signed-in account
                    data. Data sent between the app and Supabase or Open-Meteo is transmitted over encrypted connections where
                    those services support it. No method of storage or transmission is perfect, so we cannot guarantee absolute
                    security.
                </p>
            </section>

            <section>
                <h2>Weather and Location</h2>
                <p>
                    If you tap “Use My Location,” your device may ask for location permission. Chloromance uses the coordinates
                    to request weather data from Open-Meteo so it can adjust hydration estimates. You can also enter coordinates
                    manually instead of using device location. Coordinates used for weather settings may be saved locally so the
                    app can remember your selected weather area.
                </p>
            </section>

            <section>
                <h2>Notifications</h2>
                <p>
                    WiltPower reminders are optional. If enabled, Chloromance may use device notification capabilities to alert
                    you when plants become thirsty or critical. You can disable notifications in the app or in your device
                    settings.
                </p>
            </section>

            <section>
                <h2>Sharing</h2>
                <p>
                    We do not sell your personal information. We do not use your plant data for advertising. We may share limited
                    information with service providers only as needed to operate the app, such as Supabase for account
                    authentication and plant backup, and Open-Meteo for weather requests when you use weather-aware estimates.
                </p>
            </section>

            <section>
                <h2>Your Choices</h2>
                <ul>
                    <li>You can edit or delete plants in the app.</li>
                    <li>You can disable reminders at any time.</li>
                    <li>You can deny or revoke location permission in your device settings.</li>
                    <li>You can sign out of your account from the app.</li>
                    <li>
                        You can request account or cloud plant data deletion by emailing{' '}
                        <a href="mailto:praptisetty11@gmail.com">praptisetty11@gmail.com</a> from the email address associated
                        with your Chloromance account.
                    </li>
                </ul>
            </section>

            <section>
                <h2>Data Retention</h2>
                <p>
                    Chloromance keeps cloud-backed account and plant records while your account is active or as needed to provide
                    the app. Plants you delete in the app are removed from your cloud-backed plant records. If you request account
                    or cloud plant data deletion, we will delete or anonymize the relevant records unless retention is required
                    for legal, security, or abuse-prevention reasons.
                </p>
            </section>

            <section>
                <h2>Children</h2>
                <p>
                    Chloromance is not directed to children under 13. If you believe a child has provided personal information,
                    contact us so we can review and delete it where appropriate.
                </p>
            </section>

            <section>
                <h2>Changes</h2>
                <p>
                    We may update this Privacy Policy as Chloromance changes. If Chloromance adds payment processing, Stripe
                    subscriptions, analytics, or new data-sharing features, this policy should be updated before release.
                </p>
            </section>
        </>
    );
}

function PrivacyPolicy() {
    return (
        <article className="page privacy-policy-page">
            <p className="eyebrow">Chloromance</p>
            <h1>Privacy Policy</h1>
            <PrivacyPolicyContent />
        </article>
    );
}

export default PrivacyPolicy;
